/**
 * BranchGuardContext — soft prompt offering to create a branch
 * when a user mutates data on `main`.
 *
 * Usage:
 *   const { guard } = useBranchGuard();
 *   guard(() => doMutation(), { hint: "ref-area" });
 *
 * If the user is on a feature branch, the callback runs immediately.
 * If on main, a dialog appears offering: create branch, continue on main, or cancel.
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useProvenanceContext } from "./ProvenanceContext";
import { useCreateBranch } from "../hooks/useVersioning";
import { useAnonymous } from "./AnonymousContext";

const SESSION_KEY = "thread:branchGuardDismissed";

export interface GuardOptions {
  hint?: string;
}

export type GuardChoice = "branch" | "main" | "cancel";

interface PendingGuard {
  callback: () => void | Promise<void>;
  resolve: (choice: GuardChoice) => void;
  suggestedName: string;
}

export interface BranchGuardContextValue {
  guard: (callback: () => void | Promise<void>, options?: GuardOptions) => Promise<GuardChoice>;
}

const BranchGuardCtx = createContext<BranchGuardContextValue | null>(null);

export function useBranchGuard(): BranchGuardContextValue {
  const ctx = useContext(BranchGuardCtx);
  if (!ctx) throw new Error("useBranchGuard must be inside BranchGuardProvider");
  return ctx;
}

function generateBranchName(hint?: string): string {
  const date = new Date().toISOString().slice(0, 10);
  const slug = hint?.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase();
  return slug ? `edit/${slug}-${date}` : `edit/main-${date}`;
}

interface ProviderProps {
  children: ReactNode;
}

export function BranchGuardProvider({ children }: ProviderProps) {
  const { currentBranch } = useProvenanceContext();
  const { isAnonymous } = useAnonymous();
  const [pending, setPending] = useState<PendingGuard | null>(null);
  const pendingRef = useRef<PendingGuard | null>(null);

  const guard = useCallback(
    (callback: () => void | Promise<void>, options?: GuardOptions): Promise<GuardChoice> => {
      // Anonymous users never create server branches — run callback directly
      if (isAnonymous) {
        callback();
        return Promise.resolve("main" as GuardChoice);
      }

      if (currentBranch !== "main") {
        callback();
        return Promise.resolve("main" as GuardChoice);
      }

      if (sessionStorage.getItem(SESSION_KEY) === "true") {
        callback();
        return Promise.resolve("main" as GuardChoice);
      }

      return new Promise<GuardChoice>((resolve) => {
        const entry: PendingGuard = {
          callback,
          resolve,
          suggestedName: generateBranchName(options?.hint),
        };
        pendingRef.current = entry;
        setPending(entry);
      });
    },
    [currentBranch, isAnonymous],
  );

  const _resolve = useCallback((choice: GuardChoice) => {
    const entry = pendingRef.current;
    if (!entry) return;
    pendingRef.current = null;
    setPending(null);
    entry.resolve(choice);
  }, []);

  return (
    <BranchGuardCtx.Provider value={{ guard }}>
      {children}
      <BranchGuardDialogInner pending={pending} onResolve={_resolve} />
    </BranchGuardCtx.Provider>
  );
}

// ---------------------------------------------------------------------------
// Inline dialog — keeps all guard state co-located in the provider file.
// ---------------------------------------------------------------------------

import { Button, Checkbox, Dialog, Flex, Text, TextField } from "@radix-ui/themes";

const BRANCH_NAME_RE = /^[a-zA-Z0-9][a-zA-Z0-9._/-]*$/;

function BranchGuardDialogInner({
  pending,
  onResolve,
}: {
  pending: PendingGuard | null;
  onResolve: (choice: GuardChoice) => void;
}) {
  const { projectId, setBranch } = useProvenanceContext();
  const createMutation = useCreateBranch(projectId);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dismiss, setDismiss] = useState(false);
  const prevSuggested = useRef<string | null>(null);

  if (pending && pending.suggestedName !== prevSuggested.current) {
    prevSuggested.current = pending.suggestedName;
    setName(pending.suggestedName);
    setError(null);
    setDismiss(false);
  }

  function handleContinueOnMain() {
    if (dismiss) sessionStorage.setItem(SESSION_KEY, "true");
    const cb = pending?.callback;
    onResolve("main");
    cb?.();
  }

  function handleCreateBranch() {
    const trimmed = name.trim();
    if (!trimmed) { setError("Branch name is required"); return; }
    if (!BRANCH_NAME_RE.test(trimmed)) { setError("Use letters, numbers, dashes, dots, underscores, or slashes"); return; }
    if (trimmed === "main") { setError("Cannot name a branch 'main'"); return; }

    const cb = pending?.callback;
    createMutation.mutate({ name: trimmed }, {
      onSuccess: () => {
        setBranch(trimmed);
        onResolve("branch");
        cb?.();
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : String(err));
      },
    });
  }

  function handleCancel() {
    onResolve("cancel");
  }

  return (
    <Dialog.Root
      open={!!pending}
      onOpenChange={(open) => { if (!open) handleCancel(); }}
    >
      <Dialog.Content maxWidth="440px">
        <Dialog.Title>You're editing on main</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Creating a branch keeps main clean and gives you an isolated workspace
          for this change.
        </Dialog.Description>

        <Flex direction="column" gap="3" mt="4">
          <TextField.Root
            placeholder="edit/my-experiment"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreateBranch(); }}
          />

          {error && <Text size="1" color="red">{error}</Text>}

          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <Checkbox checked={dismiss} onCheckedChange={(v) => setDismiss(v === true)} />
            <Text size="1" color="gray">Don't ask again this session</Text>
          </label>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Button variant="soft" color="gray" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="soft" onClick={handleContinueOnMain}>
            Continue on main
          </Button>
          <Button onClick={handleCreateBranch} disabled={createMutation.isPending}>
            {createMutation.isPending ? "Creating..." : "Create branch & save"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
